/**
 * Scroll animations.
 *
 * One IntersectionObserver that flips a data attribute. All easing, distance
 * and timing live in CSS tokens, so this file stays small and nothing is
 * animated from JavaScript directly.
 *
 * Safety contract: the CSS only hides content under `.animations-ready`,
 * which this file adds. If the script never runs, throws, or the observer
 * never fires, content stays visible. An effect failing must never cost the
 * visitor the page.
 *
 * Contract:
 *   [data-animate="fade-up"]        an element to animate
 *   [data-animate-group]            stagger direct [data-animate] descendants
 *   [data-animate-visible="true"]   set here when the element enters
 */
(function () {
  'use strict';

  const root = document.documentElement;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function revealAll() {
    root.classList.remove('animations-ready');
    document.querySelectorAll('[data-animate]').forEach((el) => {
      el.setAttribute('data-animate-visible', 'true');
    });
  }

  if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  const repeat = root.dataset.animationsRepeat === 'true';
  const staggerMs = parseInt(root.dataset.animationsStagger || '0', 10);
  let hasRevealedSomething = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          hasRevealedSomething = true;
          entry.target.setAttribute('data-animate-visible', 'true');
          if (!repeat) observer.unobserve(entry.target);
        } else if (repeat) {
          entry.target.removeAttribute('data-animate-visible');
        }
      });
    },
    // Fire slightly before the element reaches the viewport edge so the
    // animation is already running when it becomes visible.
    { rootMargin: '0px 0px -48px 0px', threshold: 0 }
  );

  /**
   * Assigns each element in a group an increasing delay so children cascade.
   * Written once as an inline custom property, not per frame.
   */
  function applyStagger(scope) {
    scope.querySelectorAll('[data-animate-group]').forEach((group) => {
      group.querySelectorAll(':scope > [data-animate]').forEach((item, index) => {
        item.style.setProperty('--animation-item-delay', `${index * staggerMs}ms`);
      });
    });
  }

  function observe(scope = document) {
    applyStagger(scope);
    scope.querySelectorAll('[data-animate]:not([data-animate-visible])').forEach((el) => {
      observer.observe(el);
    });
  }

  root.classList.add('animations-ready');
  observe();

  /**
   * Failsafe. In an inert tab, a browser with a broken observer, or any case
   * where callbacks simply never arrive, the first screen would stay blank.
   * If nothing at all has been revealed shortly after load, give up on the
   * effect and show everything.
   */
  window.addEventListener('load', () => {
    setTimeout(() => {
      if (!hasRevealedSomething) {
        observer.disconnect();
        revealAll();
      }
    }, 1200);
  });

  // The theme editor swaps section markup without a page load.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('[data-animate]').forEach((el) => {
        el.setAttribute('data-animate-visible', 'true');
      });
    });
  }

  // If the visitor turns reduced motion on mid-session, stop animating.
  prefersReducedMotion.addEventListener('change', (event) => {
    if (event.matches) {
      observer.disconnect();
      revealAll();
    }
  });

  window.Theme = window.Theme || {};
  window.Theme.animations = { observe };
})();
